import { FEEDBACK_KIND_LABEL, fmtMonthDayYear, fmtTimeZ, type PillTone } from '@concord/domain';
import type { FeedbackView } from '@concord/domain/contracts';
import { Button, Crumbs, Notice, PageHead, Panel, PanelHead, Pill, useToast } from '@concord/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import type { Me } from '../../lib/session';
import { ConsoleLayout } from '../console/ConsoleLayout';
import s from './feedback.module.css';

const TONE: Record<FeedbackView['kind'], PillTone> = { bug: 'bad', idea: 'acc', feedback: 'wait' };

export const feedbackKey = (filter: string) => ['feedback', filter] as const;

/** What people sent from the Feedback button. Staff only. */
export function FeedbackPage({ me }: { me: Me }) {
  const [params, setParams] = useSearchParams();
  const filter = params.get('filter') === 'all' ? 'all' : 'new';
  const qc = useQueryClient();
  const toast = useToast();
  const res = useQuery({
    queryKey: feedbackKey(filter),
    queryFn: () => api<{ items: FeedbackView[]; newCount: number }>(`/staff/feedback?filter=${filter}`),
  });
  const review = useMutation({
    mutationFn: (v: { id: string; reviewed: boolean }) =>
      api(`/staff/feedback/${v.id}/reviewed`, { body: { reviewed: v.reviewed } }),
    onSuccess: (_d, v) => {
      toast(v.reviewed ? 'Marked reviewed.' : 'Moved back to new.');
      void qc.invalidateQueries({ queryKey: ['feedback'] });
    },
  });
  const items = res.data?.items ?? [];

  return (
    <ConsoleLayout me={me} current="feedback">
      <Crumbs items={[{ label: 'Feedback' }]} />
      <PageHead
        title="Feedback"
        when="Sent from the Feedback button by patients, physicians and staff. Only super admins see this page."
      />
      <div className={s.filters} role="radiogroup" aria-label="Show">
        {(['new', 'all'] as const).map((f) => (
          // biome-ignore lint/a11y/useSemanticElements: filter chips
          <button
            key={f}
            type="button"
            role="radio"
            aria-checked={filter === f}
            className={filter === f ? s.on : undefined}
            onClick={() => setParams(f === 'new' ? {} : { filter: f })}
          >
            {f === 'new' ? `New (${res.data?.newCount ?? 0})` : 'All'}
          </button>
        ))}
      </div>
      {res.isError && <Notice tone="bad">{errorMessage(res.error)}</Notice>}
      {review.isError && <Notice tone="bad">{errorMessage(review.error)}</Notice>}
      <Panel aria-label="Reports">
        <PanelHead
          title={filter === 'new' ? 'New reports' : 'All reports'}
          sub={res.data ? items.length : ''}
        />
        {res.data && items.length === 0 ? (
          <div className="pbody sub2">
            {filter === 'new' ? 'Nothing new. Reviewed reports are under All.' : 'No feedback yet.'}
          </div>
        ) : (
          <div className="tw">
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Kind</th>
                  <th>Message</th>
                  <th>From</th>
                  <th>Page and device</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((f) => (
                  <tr key={f.id}>
                    <td className={s.when}>
                      {fmtMonthDayYear(new Date(f.createdAt), me.timezone)}
                      <div className="sub2">{fmtTimeZ(new Date(f.createdAt), me.timezone)}</div>
                    </td>
                    <td>
                      <Pill tone={TONE[f.kind]}>{FEEDBACK_KIND_LABEL[f.kind]}</Pill>
                    </td>
                    <td className={s.msg}>{f.message}</td>
                    <td className={s.from}>
                      {f.from ? (
                        <>
                          {f.from.name}
                          <div className="sub2">
                            {f.from.role} · {f.from.email}
                          </div>
                        </>
                      ) : (
                        <span className="sub2">Not signed in</span>
                      )}
                    </td>
                    <td className={s.page}>
                      <div className={s.url}>{f.pageUrl || '—'}</div>
                      <div className="sub2">IP {f.ip ?? 'unknown'}</div>
                      {f.userAgent && (
                        <div className="sub2" title={f.userAgent}>
                          {f.userAgent.slice(0, 60)}
                          {f.userAgent.length > 60 ? '…' : ''}
                        </div>
                      )}
                    </td>
                    <td className={s.act}>
                      {f.reviewedAt ? (
                        <>
                          <div className="sub2">Reviewed by {f.reviewedBy ?? 'staff'}</div>
                          <Button
                            variant="ghost"
                            onClick={() => review.mutate({ id: f.id, reviewed: false })}
                          >
                            Mark new
                          </Button>
                        </>
                      ) : (
                        <Button variant="sec" onClick={() => review.mutate({ id: f.id, reviewed: true })}>
                          Mark reviewed
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </ConsoleLayout>
  );
}
